import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { TableBody as Body } from '@mui/material';

import { Badge, TableNoData, Text } from '../../shared/uiComponents';
import { HeaderProperties, TableDataProperties } from '../../shared/uiComponents/Table/tableProperties';
import { BadgeTypes } from '../../shared/uiComponents/StatusBadge';
import { CustomTableRow, RowTableCell } from '../../shared/uiComponents/Table/tableHelpers';
import CustomDrawer from '../../shared/uiComponents/Drawer';
import UserAction from '../../components/UserAction';

import { DispatchProperties } from '../../redux/store';
import { UserProperties } from '../../redux/API/userAPIProperties';
import { getUsers } from '../../redux/State/userSlice';
import { getBranchBadgeType } from '../../shared/Helpers/constants';

export interface AdminDataProperties extends TableDataProperties {
	query: UserProperties[] | null;
}

interface RowRendererProperties {
	data: UserProperties[];
}

export const Headers: HeaderProperties[] = [
	{ id: '8', name: 'Full Name', orderBy: 'fullName' },
	{ id: '1', name: 'Email', orderBy: 'email' },
	{ id: '3', name: 'Email Status', orderBy: 'emailConfirmed', width: '150px' },
	{ id: '4', name: 'Status', orderBy: 'isDeactivated', width: '100px' },
	{ id: '5', name: 'Licensed or Limited', orderBy: 'authorizationType' },
	{
		id: '6',
		name: 'Role',
	},
	{ id: '9', name: 'Branch' },
	{ id: '7', name: 'Actions', width: '120px' },
];

export const initialUserTableState: UserProperties = {
	id: '',
	firstName: '',
	lastName: '',
	fullName: '',
	patronymic: '',
	numberOfClients: 0,
	phoneNumber: '',
	address: '',
	notes: '',
	email: '',
	numberOfNotes: 0,
	role: {
		id: '',
		name: '',
		section: {
			id: 0,
			name: '',
		},
	},
	createdBy: {
		id: '',
		fullName: '',
		role: '',
		authorizationType: {
			id: 0,
			name: '',
		},
	},
	authorizationType: {
		id: 0,
		name: '',
	},
	emailConfirmed: false,
	isDeactivated: false,
	isCaseCoordinator: false,
	mbhId: '',
	caseCoordinators: [],
};

export const TableBody: FC<RowRendererProperties> = ({ data }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();

	const fetchUsers = () => {
		const page = params.get('page') || '1';
		const pageSize = params.get('pageSize') || '8';
		const orderBy = params.get('orderBy') || '';
		const direction = params.get('direction') || '';
		const searchString = params.get('search') || '';
		const section = params.get('status');

		dispatch(getUsers({ page, pageSize, orderBy, direction, searchString, section }));
	};

	if (!data?.length) {
		return <TableNoData spanScope={Headers.length} />;
	}

	return (
		<Body>
			{data.map((row, index) => (
				<CustomTableRow key={index}>
					<RowTableCell>{row.fullName}</RowTableCell>
					<RowTableCell>{row.email}</RowTableCell>
					<RowTableCell>
						<Badge
							label={row.emailConfirmed ? 'Confirmed' : 'Pending'}
							sizeType="sm"
							type={row.emailConfirmed ? 'solidSuccess' : 'solidPrimary'}
						/>
					</RowTableCell>
					<RowTableCell>
						<Badge
							label={row.isDeactivated ? 'Inactive' : 'Active'}
							sizeType="sm"
							type={row.isDeactivated ? 'gray' : 'success'}
						/>
					</RowTableCell>
					<RowTableCell>{row.authorizationType ? row.authorizationType.name : '-'}</RowTableCell>
					<RowTableCell>{row.role?.name || '-'}</RowTableCell>
					<RowTableCell>
						<div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
							{row.branches && row.branches.length > 0 ? (
								row.branches.map((branch) => (
									<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="sm" />
								))
							) : (
								<span style={{ color: '#999', fontSize: '12px' }}>None</span>
							)}
						</div>
					</RowTableCell>
					<RowTableCell>
						<UserAction item={row} fetchUsers={fetchUsers} />
					</RowTableCell>
				</CustomTableRow>
			))}
		</Body>
	);
};

const labels = ['Admin', 'BCBA', 'BT', 'All'];

enum UserRoleTypes {
	'primary', // Admin
	'success', // BCBA
	'error', // BT
}

export const UserFilters = () => {
	const [params, setParams] = useSearchParams();
	const open = params.get('filter') === 'open';

	const closeHandler = () => {
		params.set('filter', 'close');
		setParams(params, { replace: true });
	};

	const selectStatusHandler = (name: string, id: number) => {
		params.set('page', '1');

		if (name.toLowerCase() === 'all') {
			params.delete('status');
			setParams(params, { replace: true });
			closeHandler();

			return;
		}
		params.set('status', `${id}`);
		setParams(params, { replace: true });
		closeHandler();
	};

	return (
		<CustomDrawer
			open={open}
			onClose={closeHandler}
			label="Filters"
			Content={() => (
				<>
					<Text title="Select user role" type="h6" fontWeight="medium" font="header" />
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 16,
							alignItems: 'flex-start',
						}}
					>
						{Boolean(labels) &&
							labels.map((label, index) => (
								<div
									key={label}
									style={{
										cursor: 'pointer',
										width: 'max-content',
									}}
								>
									<Badge
										label={label}
										sizeType="lg"
										type={UserRoleTypes[index] as BadgeTypes}
										onClick={() => selectStatusHandler(label, index)}
									/>
								</div>
							))}
					</div>
				</>
			)}
		/>
	);
};
